import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useCountry } from '../components/CountryContext';
import API from '../api';

const ShippingRatesContext = createContext();

export const useGlobalShippingRates = () => {
  const context = useContext(ShippingRatesContext);
  if (!context) {
    throw new Error('useGlobalShippingRates deve essere usato all\'interno di ShippingRatesProvider');
  }
  return context;
};

export const ShippingRatesProvider = ({ children }) => {
  const { selectedCountry } = useCountry();
  const [shippingRates, setShippingRates] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Cache per evitare chiamate duplicate
  const cache = useRef(new Map());
  const cacheTimeout = 5 * 60 * 1000; // 5 minuti
  const pendingRequests = useRef(new Map());

  const fetchBatchShippingRates = useCallback(async (variantIds) => {
    if (!variantIds || variantIds.length === 0) return;

    // Filtra ID già in cache e validi
    const now = Date.now();
    const idsToFetch = variantIds.filter(id => {
      const cacheKey = `${id}-${selectedCountry}`;
      const cached = cache.current.get(cacheKey);
      return !cached || (now - cached.timestamp) > cacheTimeout;
    });

    if (idsToFetch.length === 0) {
      // Tutti gli ID sono in cache
      const cachedRates = {};
      variantIds.forEach(id => {
        const cacheKey = `${id}-${selectedCountry}`;
        const cached = cache.current.get(cacheKey);
        if (cached) {
          cachedRates[id] = cached.data;
        }
      });
      setShippingRates(prev => ({ ...prev, ...cachedRates }));
      return;
    }

    // Evita richieste duplicate per gli stessi ID
    const requestKey = `${idsToFetch.sort().join(',')}-${selectedCountry}`;
    if (pendingRequests.current.has(requestKey)) {
      console.log('[ShippingRates] Richiesta già in corso per:', requestKey);
      return;
    }

    console.log(`[ShippingRates] Caricamento tariffe per ${idsToFetch.length} varianti`);
    pendingRequests.current.set(requestKey, true);
    setLoading(true);
    setError(null);

    try {
      const response = await API.post('/shipping/batch', {
        variantIds: idsToFetch,
        country: selectedCountry
      });

      if (response.data.success) {
        const newRates = response.data.results;
        
        // Aggiorna cache
        Object.entries(newRates).forEach(([id, data]) => {
          const cacheKey = `${id}-${selectedCountry}`;
          cache.current.set(cacheKey, {
            data,
            timestamp: now
          });
        });

        // Aggiorna stato
        setShippingRates(prev => ({ ...prev, ...newRates }));
        console.log(`[ShippingRates] Tariffe caricate per ${Object.keys(newRates).length} varianti`);
      } else {
        setError(response.data.message || 'Errore nel recupero delle tariffe');
      }
    } catch (err) {
      console.error('Errore nel recupero delle tariffe di spedizione in batch:', err);
      setError('Errore nel recupero delle tariffe di spedizione');
    } finally {
      setLoading(false);
      pendingRequests.current.delete(requestKey);
    }
  }, [selectedCountry]);

  // Funzione per ottenere la tariffa di una variante specifica
  const getShippingRate = useCallback((variantId) => {
    const rate = shippingRates[variantId];
    if (rate) {
      return {
        ...rate,
        loading: false // Se abbiamo i dati, non è in loading
      };
    }
    
    // Se non abbiamo i dati, controlla se è in cache
    const cacheKey = `${variantId}-${selectedCountry}`;
    const cached = cache.current.get(cacheKey);
    if (cached) {
      return {
        ...cached.data,
        loading: false
      };
    }
    
    // Se non è in cache e non è in loading, potrebbe essere in corso di caricamento
    return {
      has_rates: false,
      shipping_rate: null,
      loading: loading
    };
  }, [shippingRates, loading, selectedCountry]);

  // Funzione per caricare tariffe per una lista di varianti
  const loadShippingRates = useCallback((variantIds) => {
    if (variantIds && variantIds.length > 0) {
      fetchBatchShippingRates(variantIds);
    }
  }, [fetchBatchShippingRates]);

  // Funzione per forzare il refresh delle tariffe
  const refreshRates = useCallback((variantIds) => {
    if (variantIds && variantIds.length > 0) {
      // Pulisce la cache per forzare il refresh
      variantIds.forEach(id => {
        const cacheKey = `${id}-${selectedCountry}`;
        cache.current.delete(cacheKey);
      });
      fetchBatchShippingRates(variantIds);
    }
  }, [selectedCountry, fetchBatchShippingRates]);

  // Pulisce la cache quando cambia il paese
  useEffect(() => {
    cache.current.clear();
    setShippingRates({});
  }, [selectedCountry]);

  const value = {
    shippingRates,
    loading,
    error,
    getShippingRate,
    loadShippingRates,
    refreshRates
  };

  return (
    <ShippingRatesContext.Provider value={value}>
      {children}
    </ShippingRatesContext.Provider>
  );
}; 